import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, Output, ViewChild } from '@angular/core';

/**
 * Lightweight reusable modal shell without MatDialog.
 *
 * - Default (`prerender = false`): projected content is created on open and destroyed on close,
 *   mirroring the lazy behavior of a MatDialog-based wrapper.
 * - `prerender = true`: projected content stays in the DOM while closed (hidden via visibility,
 *   not `display:none`, so it keeps its layout and stays measurable/scrollable). Lets the content
 *   be pre-rendered and kept in sync before it's revealed for a seamless open.
 *
 * Opened/closed programmatically via {@link open}/{@link close}.
 */
@Component({
    selector: 'ft-overlay-modal',
    templateUrl: './overlay-modal.component.html',
    styleUrls: ['./overlay-modal.component.scss'],
})
export class OverlayModalComponent implements AfterViewInit, OnDestroy {
    @Input() prerender = false;
    @Input() theme: 'dark' | 'light' | 'gray' = 'dark';
    @Input() hasCloseButton = true;
    @Input() closeOnBackdrop = true;

    @Output() opened = new EventEmitter<void>();
    @Output() closed = new EventEmitter<void>();

    @ViewChild('overlayEl') overlayEl: ElementRef<HTMLElement>;

    isOpen = false;

    private originalParent: HTMLElement | null = null;

    ngAfterViewInit() {
        // Render at document.body level so position:fixed is relative to the viewport and isn't
        // clipped/trapped by transformed or overflow-hidden ancestors.
        const el = this.overlayEl?.nativeElement;
        if (el) {
            this.originalParent = el.parentElement;
            document.body.appendChild(el);
        }
    }

    ngOnDestroy() {
        // Restore the node to its original parent so Angular tears the view down cleanly.
        const el = this.overlayEl?.nativeElement;
        if (el && this.originalParent) {
            this.originalParent.appendChild(el);
        }
    }

    open() {
        if (this.isOpen) {
            return;
        }
        this.isOpen = true;
        this.opened.emit();
    }

    close() {
        if (!this.isOpen) {
            return;
        }
        this.isOpen = false;
        this.closed.emit();
    }

    onBackdropClick() {
        if (this.closeOnBackdrop) {
            this.close();
        }
    }

    @HostListener('document:keydown.escape')
    onEscape() {
        this.close();
    }
}
